/**
 * The house at a glance, before sign-in.
 *
 * Unauthenticated on purpose — there is no token to send at the gate, which is the
 * whole point. Everything it returns comes from fabric-api's `/public/glance`
 * allowlist, where each entity was admitted only after answering *would this tell a
 * stranger whether the house is empty?* Solar, battery, grid, tank and outside
 * temperature do not. Indoor climate, doors, motion and the alarm are excluded at
 * that endpoint, not here.
 *
 * Fails to an empty array, which hides the strip. A login screen showing a broken
 * panel is a login screen nobody trusts, at exactly the moment they are deciding
 * whether to.
 */
const FABRIC_API = import.meta.env.VITE_FABRIC_API_URL ?? "";

export type Stat = { key: string; label: string; value: number | null; unit: string; text: string | null };

class Glance {
  stats = $state<Stat[]>([]);

  /**
   * When this client last got an answer, and what the answer said about itself.
   *
   * Added for the kitchen tablet (F1), which is the first surface that shows these
   * figures for hours at a time without anyone reloading. The sign-in screen shows
   * them for the twenty seconds somebody is signing in, so "how old is this?" never
   * came up; on a wall it is the whole question. Two different ages are tracked
   * because they fail differently:
   *
   *  · `readAt` — when *we* last succeeded. Null means we have never had an answer,
   *    which is not the same as zero.
   *  · `serverStale` — the endpoint's own flag, set when its BigQuery read failed
   *    and it served its last good cache rather than an error.
   *
   * A figure that is old is still shown, but never without its age beside it. The
   * lie the acceptance list names is a cached figure presented as live, not a
   * cached figure.
   */
  readAt = $state<number | null>(null);
  serverStale = $state(false);
  /** Set when a read fails. The last figures stay on screen, labelled. */
  error = $state<string | null>(null);

  constructor() {
    if (!FABRIC_API) return;
    void this.load();
  }

  async load(): Promise<void> {
    try {
      // No Authorization header, and no `credentials` — that is what makes the
      // endpoint's wildcard CORS safe.
      const res = await fetch(`${FABRIC_API}/public/glance`, { signal: AbortSignal.timeout(8000) });
      if (!res.ok) {
        this.error = `Fabric answered ${res.status}`;
        return;
      }
      const body = (await res.json()) as { stats?: Stat[]; stale?: boolean };
      this.stats = Array.isArray(body.stats) ? body.stats : [];
      this.serverStale = body.stale === true;
      this.readAt = Date.now();
      this.error = null;
    } catch {
      // Leave the figures alone and record the failure. Blanking them would turn a
      // network blip into "the house has no readings", which is a different claim.
      this.error = "Could not reach Fabric";
    }
  }

  /** One stat by key, or null. Null is what makes a caller render an em dash. */
  stat(key: string): Stat | null {
    return this.stats.find((s) => s.key === key) ?? null;
  }
}

export const glance = new Glance();
